import React, { useState, useEffect } from 'react';
import { Quote } from '../types';
import { supabase } from '../supabaseClient';
import { clientView, saveClientPortal } from '../clientAccess';
import { GlassCard } from './ui/GlassCard';
import { Info, Loader2, CheckCircle, Upload, Calendar, MapPin, FileText, ArrowRight, XCircle, Download, LayoutList, Clock, Copy, Link as LinkIcon, Package, ChevronDown, ChevronUp } from 'lucide-react';

import {buildContract, assessContract, fieldState, key as blueprintKey, BlueprintContract} from '../blueprint/contract';

interface ClientPortalProps {
  quoteId: string;
}

export const ClientPortal: React.FC<ClientPortalProps> = ({ quoteId }) => {
  const getNamespacedKey = blueprintKey;
  const [contract,setContract] = useState<BlueprintContract>(()=>buildContract());
  const [submissionError,setSubmissionError] = useState('');
  const [saveNotice,setSaveNotice] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [blueprintSections, setBlueprintSections] = useState<Record<string, any[]>>({});
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});

  // ⚡ NEW: ACCORDION & PROGRESS TRACKING STATE
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [openSection, setOpenSection] = useState<string | null>('INITIAL');

  useEffect(() => {
    const fetchPortalData = async () => {
      try {
        const scoped = await clientView(quoteId.trim());
        const quoteData = scoped.quote;
        const cleanQuote = {
            ...quoteData,
            eventDate: quoteData.event_date || quoteData.eventDate,
            eventLocation: quoteData.event_location || quoteData.eventLocation,
            eventStartTime: quoteData.event_start_time,
            eventEndTime: quoteData.event_end_time,
            setupTime: quoteData.setup_time,
            packDownTime: quoteData.pack_down_time,
            logistics: quoteData.logistics || {}, 
            notes: quoteData.additional_details || quoteData.notes,
            client: {
                firstName: quoteData.client_first_name,
                lastName: quoteData.client_last_name,
                company: quoteData.client_company,
            },
            items: quoteData.items || []
        };
        setQuote(cleanQuote);

        if (quoteData.onboarding_data) {
            setFormData(quoteData.onboarding_data);
            if (quoteData.onboarding_data._completed_sections) {
                setCompletedSections(quoteData.onboarding_data._completed_sections);
            }
        }

        const currentContract = buildContract(cleanQuote.items, scoped.products || [], quoteData.portal_blueprint || []);
        setContract(currentContract);
        setBlueprintSections(currentContract.sections);

} catch (err) {
        console.error("Portal Error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (quoteId) fetchPortalData();
  }, [quoteId]);

  const handleInputChange = (sectionName: string, fieldLabel: string, value: any) => {
    const key = getNamespacedKey(sectionName, fieldLabel);
    setFormData(prev => ({ ...prev, [key]: value }));
    setSaveNotice('');setSubmissionError('');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, sectionName: string, fieldLabel: string) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !quote) return;

    const fieldKey = getNamespacedKey(sectionName, fieldLabel);
    setUploadingFields(prev => ({ ...prev, [fieldKey]: true }));
    
    try {
        const uploadedUrls: string[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const cleanLabel = fieldLabel.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
            const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
            const filePath = `${quote.id}/${cleanLabel}_${Date.now()}_${cleanFileName}`;

            const { error: uploadError } = await supabase.storage.from('production-assets').upload(filePath, file);
            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from('production-assets').getPublicUrl(filePath);
            uploadedUrls.push(data.publicUrl);
        }

        setFormData(prev => {
            const existing = prev[fieldKey];
            const currentUrls = Array.isArray(existing) ? existing : (existing ? [existing] : []);
            return { ...prev, [fieldKey]: [...currentUrls, ...uploadedUrls] };
        });
        
    } catch (err) {
        console.error("Upload failed", err);
        alert("Failed to upload files. Please try again.");
    } finally {
        setUploadingFields(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleCopySettings = (e: React.MouseEvent, fromSection: string, toSection: string) => {
      e.preventDefault();
      
      const newFormData = { ...formData };
      const fieldsToCopy = [
        '1. Delivery Methods',
        'Email Subject Line',
        'Email Body Copy',
        'Email From Name',
        'Reply-To Email Address',
        'SMS / Text Message Copy',
        '2. Guest Sharing & Download Options',
        '3. Live Event Gallery',
        'Event Hashtag'
    ];

      fieldsToCopy.forEach(label => {
          const sourceKey = getNamespacedKey(fromSection, label);
          const targetKey = getNamespacedKey(toSection, label);
          if (formData[sourceKey]) {
              newFormData[targetKey] = formData[sourceKey];
          }
      });

      setFormData(newFormData);
      alert("Settings copied successfully!");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quote || !quote.id) return;

    const review = assessContract(contract,formData);
    if (!review.canSubmit) {
      setSubmissionError(review.missing.length ? `Please complete ${review.missing.length} required answer(s), including those in collapsed sections. Your saved progress is retained.` : 'Some question definitions need Quikpix review. You can still save your progress.');
      return;
    }
    setSubmissionError('');
    setIsSubmitting(true);
    
    try {
        await saveClientPortal(quote.id, formData, true);
        setIsSubmitted(true);
    } catch (err) {
        console.error("Submission failed", err);
        setSubmissionError(err instanceof Error ? err.message : 'Unable to submit. Your saved progress is retained.');
    } finally {
        setIsSubmitting(false);
    }
  };

  if (isLoading) {
      return (
          <div className="min-h-screen bg-slate-950 flex items-center justify-center">
              <Loader2 className="text-brand-neonBlue animate-spin" size={48} />
          </div>
      );
  }

  if (!quote) {
      return (
          <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
              <XCircle className="text-red-500 mb-4" size={64} />
              <h2 className="text-2xl font-bold mb-2">Event Not Found</h2>
              <p className="text-slate-400">This magic link appears to be invalid or expired.</p>
          </div>
      );
  }

  if (isSubmitted) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
            <GlassCard className="max-w-xl w-full p-12 text-center border-green-500/30">
                <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_50px_rgba(34,197,94,0.3)]">
                    <CheckCircle className="text-green-400" size={48} />
                </div>
                <h2 className="text-4xl font-display font-bold text-white mb-4 tracking-tight">Blueprint submitted</h2>
                <p className="text-slate-400 text-lg leading-relaxed">
                    Your required answers have been saved for Quikpix review. You can return using this link to update your details.
                </p>
            </GlassCard>
        </div>
      );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-20 px-4">
      <div className="max-w-3xl mx-auto">
          
          <div className="text-center mb-12 animate-fade-in-up">
              <div className="text-2xl font-bold text-brand-neonBlue mb-6 tracking-widest uppercase">QUIKPIX</div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 tracking-tight">
                  Event Onboarding
              </h1>
              <p className="text-xl text-slate-400">
                  Welcome, {quote.client?.firstName}! Let's finalize the details for {quote.title}.
              </p>
          </div>
          {/* ⚡ NEW: PORTAL INSTRUCTIONS WIDGET ⚡ */}
          <div className="mb-8 p-6 bg-brand-neonBlue/5 border border-brand-neonBlue/20 rounded-[32px] animate-fade-in-up shadow-inner">
              <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-brand-neonBlue/10 flex items-center justify-center shrink-0 border border-brand-neonBlue/20">
                      <Info size={24} className="text-brand-neonBlue" />
                  </div>
                  <div>
                      <h3 className="text-brand-neonBlue font-bold text-lg tracking-tight mb-2">How this Workspace Works</h3>
                      <p className="text-sm text-slate-300 leading-relaxed">
                          This portal is a <strong>live, collaborative blueprint</strong>. You do not need to complete it all at once! 
                          You can securely share this exact link with your graphic designer, event planner, or team members so they can 
                          upload artwork directly or assist with technical questions.
                          <br/><br/>
                          Use <strong>Save progress without submitting</strong> whenever you need to pause. Choose <strong>Save & Submit Details</strong> when all required answers are complete.
                      </p>
                  </div>
              </div>
          </div>
          <GlassCard className="p-6 mb-8 flex flex-col sm:flex-row justify-around items-center gap-6 border-white/5 animate-fade-in-up">
              <div className="text-center">
                  <Calendar className="text-brand-neonBlue mx-auto mb-2" size={24} />
                  <p className="text-white font-bold">{quote.eventDate ? new Date(quote.eventDate).toLocaleDateString('en-NZ', { weekday: 'long', month: 'long', day: 'numeric' }) : 'Date TBD'}</p>
              </div>
              
              <div className="hidden sm:block w-px h-12 bg-white/10"></div>
              
              <div className="text-center">
                  <MapPin className="text-brand-neonBlue mx-auto mb-2" size={24} />
                  <p className="text-white font-bold">{quote.eventLocation || 'Venue TBD'}</p>
              </div>

              <div className="hidden sm:block w-px h-12 bg-white/10"></div>
              
              <div className="text-center">
                  <Clock className="text-brand-neonBlue mx-auto mb-2" size={24} />
                  <p className="text-white font-bold">
                      {(quote as any).eventStartTime && (quote as any).eventEndTime 
                          ? `${(quote as any).eventStartTime.slice(0,5)} - ${(quote as any).eventEndTime.slice(0,5)}` 
                          : (quote.logistics?.hours ? `${quote.logistics.hours} Hours` : 'Time TBD')}
                  </p>
                  {((quote as any).setupTime || (quote as any).packDownTime) && (
                      <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider font-bold">
                          In: {(quote as any).setupTime?.slice(0,5) || '--'} | Out: {(quote as any).packDownTime?.slice(0,5) || '--'}
                      </p>
                  )}
              </div>
          </GlassCard>

          {quote.notes && (
              <div className="mb-12 p-6 bg-brand-neonBlue/5 border border-brand-neonBlue/20 rounded-2xl animate-fade-in-up delay-100">
                  <h4 className="text-[10px] font-black text-brand-neonBlue uppercase tracking-widest mb-3">Event Notes from Quikpix</h4>
                  <p className="text-sm text-slate-300 italic whitespace-pre-wrap">{quote.notes}</p>
              </div>
          )}

          {saveNotice && <p role="status" className="mb-4 text-emerald-300">{saveNotice}</p>}
          <form noValidate onSubmit={handleSubmit} className="space-y-8 animate-fade-in-up" autoComplete="off">
          {submissionError && <div role="alert" className="mb-5 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200"><p>{submissionError}</p><ul className="mt-2 list-disc pl-5">{assessContract(contract,formData).missing.map(f=><li key={f.key}>{f.section} — {f.label}</li>)}</ul></div>}
          <button type="button" disabled={isSubmitting || ['COMPLETE','COMPLETED'].includes(String(quote.status))} onClick={async()=>{try{await saveClientPortal(quote!.id,formData);setSaveNotice('Progress saved. You can return using your existing link.');setSubmissionError('');}catch{setSubmissionError('Unable to save progress. Please keep this page open.');}}} className="mb-5 min-h-11 rounded-xl border border-white/20 px-4 text-white">Save progress without submitting</button>
          <fieldset disabled={['COMPLETE','COMPLETED'].includes(String(quote.status))}>
          {['COMPLETE','COMPLETED'].includes(String(quote.status)) && <p className="text-slate-300 mb-4">These saved event details are read-only after completion.</p>}
              
{/* DYNAMIC SECTIONS */}
{Object.entries(blueprintSections).map(([sectionName, fields], sectionIndex, array) => {
                  const isLogisticsBlock = sectionName.match(/^[1-5]\./);
                  const isOpen = openSection === sectionName || (openSection === 'INITIAL' && sectionIndex === 0);
                  const isCompleted = completedSections.includes(sectionName);
                  const nextSectionName = array[sectionIndex + 1]?.[0] || null;
                  
                  return (
                      <GlassCard 
                          key={sectionName} 
                          className={`p-6 md:p-8 transition-all duration-300 ${!isLogisticsBlock ? 'border-brand-neonPink/30 bg-brand-neonPink/5' : 'border-white/10'} ${!isOpen ? 'cursor-pointer hover:bg-white/5 opacity-80 hover:opacity-100' : 'shadow-2xl ring-1 ring-white/10'}`}
                          onClick={() => !isOpen && setOpenSection(sectionName)}
                      >
                          <div className={`flex justify-between items-center ${isOpen ? 'mb-6 border-b border-white/5 pb-4' : ''}`}>
                              <h3 className="text-xl md:text-2xl font-display font-bold text-white flex items-center gap-3">
                                  {!isLogisticsBlock ? <Package className="text-brand-neonPink" size={24} /> : <LayoutList className="text-brand-neonBlue" size={24} />}
                                  {sectionName}
                              </h3>
                              <div className="flex items-center gap-4">
                                  {isCompleted && (
                                      <span className="bg-green-500/20 text-green-400 border border-green-500/30 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                                          <CheckCircle size={14}/> Done
                                      </span>
                                  )}
                                  <button type="button" onClick={(e) => { e.stopPropagation(); setOpenSection(isOpen ? null : sectionName); }} className="text-slate-500 hover:text-white p-2">
                                      {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                                  </button>
                              </div>
                          </div>
                          
                          {isOpen && (
                              <div className="space-y-6 animate-fade-in">
                                  {fields.map((field: any) => {
                                      const fieldKey = getNamespacedKey(sectionName, field.label);
                                      
                                      // ⚡ UNIFIED DOCS RENDERER
                                      if (field.type === 'document-group') {
                                          return (
                                              <div key={field.id} className="mb-8 p-6 bg-slate-900/50 border border-brand-neonBlue/30 rounded-2xl shadow-inner animate-fade-in">
                                                  <h4 className="text-xs font-black text-brand-neonBlue uppercase tracking-widest mb-4 flex items-center gap-2">
                                                      <Download size={14}/> {field.label}
                                                  </h4>
                                                  <div className="flex flex-wrap gap-3">
                                                      {field.docs.map((doc: any, i: number) => (
                                                          <a 
                                                              key={i} 
                                                              href={doc.url} 
                                                              target="_blank" 
                                                              rel="noreferrer"
                                                              className="flex items-center gap-2 bg-slate-950 border border-white/10 hover:border-brand-neonBlue px-4 py-3 rounded-xl text-sm text-white transition-all shadow-sm group"
                                                          >
                                                              {doc.isExternal ? <LinkIcon size={16} className="text-brand-neonBlue group-hover:text-white transition-colors" /> : <FileText size={16} className="text-brand-neonBlue group-hover:text-white transition-colors" />}
                                                              {doc.name}
                                                          </a>
                                                      ))}
                                                  </div>
                                              </div>
                                          );
                                      }

                                      // ⚡ COPY SETTINGS BUTTON
                                      if (field.type === 'meta' && field.label === '_META_DUPLICATE_BTN_') {
                                          return (
                                              <div key={field.id} className="animate-fade-in mb-6 border-t border-brand-neonPink/20 pt-6 mt-6">
                                                  <button 
                                                      type="button"
                                                      onClick={(e) => handleCopySettings(e, field.copyFromPrefix, field.targetPrefix)}
                                                      className="flex items-center gap-2 bg-brand-neonBlue/20 text-brand-neonBlue border border-brand-neonBlue/30 hover:bg-brand-neonBlue hover:text-black px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all w-full justify-center shadow-neon-blue-sm"
                                                  >
                                                      <Copy size={14}/> Copy Sharing Setup from First Product
                                                  </button>
                                                  <p className="text-[9px] text-center text-slate-500 mt-2 uppercase tracking-widest">Saves you typing it out twice!</p>
                                              </div>
                                          );
                                      }

                                      const currentField = contract.questions.find(q=>q.id===field.id && q.section===sectionName);
                                      const state = currentField ? fieldState(currentField,formData,contract.questions) : {visible:true,required:false};
                                      if (state.visible === false) return null;
                                      return (
                                          <div key={field.id} className="space-y-2 animate-fade-in">
                                              <label className="text-sm font-bold text-slate-300 flex items-center gap-2">
                                                  {field.label} {state.required && <span className="text-brand-neonPink">*</span>}
                                              </label>
                                              
                                              {field.helpText && <p className="text-xs text-slate-500 mb-2 italic">{field.helpText}</p>}

                                              {field.type === 'text' && (
                                                  <input 
                                                    id={`input_${field.id}`}
                                                    name={`name_${field.id}`}
                                                    autoComplete="new-password"
                                                    required={state.required}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" 
                                                    value={formData[fieldKey] || ''}
                                                    onChange={e => handleInputChange(sectionName, field.label, e.target.value)}
                                                  />
                                              )}

                                              {field.type === 'textarea' && (
                                                  <textarea 
                                                    id={`input_${field.id}`}
                                                    name={`name_${field.id}`}
                                                    autoComplete="new-password"
                                                    required={state.required}
                                                    rows={3}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors" 
                                                    value={formData[fieldKey] || ''}
                                                    onChange={e => handleInputChange(sectionName, field.label, e.target.value)}
                                                  />
                                              )}

                                              {field.type === 'select' && (
                                                  <select 
                                                    id={`input_${field.id}`}
                                                    name={`name_${field.id}`}
                                                    autoComplete="off"
                                                    required={state.required}
                                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white outline-none focus:border-brand-neonBlue transition-colors appearance-none cursor-pointer" 
                                                    value={formData[fieldKey] || ''}
                                                    onChange={e => handleInputChange(sectionName, field.label, e.target.value)}
                                                  >
                                                      <option value="">Select an option...</option>
                                                      {field.options?.split(',').map((opt: string) => (
                                                          <option key={opt.trim()} value={opt.trim()}>{opt.trim()}</option>
                                                      ))}
                                                  </select>
                                              )}

                                              {field.type === 'upload' && (
                                                  <div className="space-y-3">
                                                      <div className="relative">
                                                          <input 
                                                            type="file" 
                                                            id={`file-${field.id}`}
                                                            name={`file_${field.id}`}
                                                            className="hidden" 
                                                            multiple 
                                                            onChange={e => handleFileUpload(e, sectionName, field.label)} 
                                                          />
                                                          <label 
                                                            htmlFor={`file-${field.id}`}
                                                            className={`flex items-center justify-center w-full p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${formData[fieldKey] && formData[fieldKey].length > 0 ? 'border-brand-neonBlue/50 bg-brand-neonBlue/5' : 'border-slate-700 hover:border-brand-neonBlue hover:bg-slate-900/50'}`}
                                                          >
                                                              {uploadingFields[fieldKey] ? (
                                                                  <span className="flex items-center gap-2 text-brand-neonBlue"><Loader2 className="animate-spin" size={20}/> Uploading files...</span>
                                                              ) : (
                                                                  <span className="flex items-center gap-2 text-slate-400"><Upload size={20}/> Click to browse and upload files</span>
                                                              )}
                                                          </label>
                                                      </div>
                                                      
                                                      {formData[fieldKey] && (Array.isArray(formData[fieldKey]) ? formData[fieldKey] : [formData[fieldKey]]).map((url: string, idx: number) => {
                                                          const fileNameMatch = url.match(/_([^_]+)$/);
                                                          const displayFileName = fileNameMatch ? fileNameMatch[1] : `Uploaded File ${idx + 1}`;
                                                          
                                                          return (
                                                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/80 border border-green-500/30 rounded-lg animate-fade-in">
                                                                  <div className="flex items-center gap-3 overflow-hidden">
                                                                      <CheckCircle size={16} className="text-green-500 shrink-0"/>
                                                                      <span className="text-sm text-slate-300 truncate">{displayFileName}</span>
                                                                  </div>
                                                                  <a href={url} target="_blank" rel="noreferrer" className="text-xs text-brand-neonBlue hover:text-brand-neonPink shrink-0 ml-4 font-bold">
                                                                      View
                                                                  </a>
                                                              </div>
                                                          );
                                                      })}
                                                  </div>
                                              )}
                                          </div>
                                      );
                                  })}

                                  {/* ⚡ THE NEW SECTION COMPLETE BUTTON */}
                                  <div className="mt-8 pt-6 border-t border-white/10 flex justify-end">
                                      <button 
                                          type="button" 
                                          onClick={async (e) => { 
                                              e.stopPropagation(); 
                                              
                                              // ⚡ BULLETPROOF ARRAY LOGIC (Fixes the iterable error)
                                              const safeCompleted = Array.isArray(completedSections) ? completedSections : [];
                                              const newCompleted = safeCompleted.includes(sectionName) 
                                                  ? safeCompleted 
                                                  : [...safeCompleted, sectionName];

                                              setCompletedSections(newCompleted);
                                              setOpenSection(nextSectionName); // Automatically open the next folder!
                                              
                                              // Silent save to database so they don't lose their green ticks
                                              if (quote?.id) {
                                                  await saveClientPortal(quote.id, { ...formData, _completed_sections: newCompleted });
                                              }
                                          }} 
                                          className="bg-green-500 text-slate-950 hover:bg-green-400 font-bold px-6 py-3 rounded-xl shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-transform hover:scale-105 flex items-center"
                                      >
                                           Save & Continue to Next Step <CheckCircle size={18} className="ml-2" />
                                      </button>
                                  </div>

                              </div>
                          )}
                      </GlassCard>
                  );
              })}

              <div className="flex flex-col items-end pt-8">
                  <p className="text-slate-400 text-xs mb-4 italic">
                      Missing information? Save your progress and return using this link. Submit when the required answers are complete.
                  </p>
                  
                  <button 
                    type="submit" 
                    disabled={isSubmitting || Object.values(uploadingFields).some(isUploading => isUploading)}
                    className="w-full md:w-auto !bg-[#22d3ee] hover:!bg-[#67e8f9] !text-black font-black text-lg px-12 py-5 rounded-2xl shadow-[0_0_30px_rgba(34,211,238,0.4)] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                      {isSubmitting ? <><Loader2 className="animate-spin mr-2" size={20}/> Saving...</> : <>Save & Submit Details <ArrowRight className="ml-2" size={20}/></>}
                  </button>
              </div>
          </fieldset></form>
      </div>
    </div>
  );
};
